import React, { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { CgIfDesign } from "react-icons/cg";
import axios from "axios";
import ProfileError from "./ProfileError";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const PIE_COLORS = [
  "#4386F4",
  "#56D6C1",
  "#ECBF5B",
  "#EEA849",
  "#E6588E",
  "#6ED96E",
  "#FC6F7C",
  "#7D53DE",
];
const SIZE_COLORS = ["#4082EA", "#56D6C1", "#F7CF61", "#F7A045", "#EC698E"];

function getSizeCategory(size) {
  if (size < 10 * 1024) return "Tiny (<10KB)";
  if (size < 100 * 1024) return "Small (10KB-100KB)";
  if (size < 1024 * 1024) return "Medium (100KB-1MB)";
  if (size < 10 * 1024 * 1024) return "Large (1MB-10MB)";
  return "Huge (>10MB)";
}

const CustomLanguageTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const total = payload[0].payload.total || 1;
    const percentage = ((data.value / total) * 100).toFixed(0);
    return (
      <div className="bg-gray-800 text-white px-3 py-2 rounded shadow-lg text-sm">
        <p className="font-bold">{data.name}</p>
        <p>{`${data.value} repos (${percentage}%)`}</p>
      </div>
    );
  }
  return null;
};

const CustomSizeTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-gray-800 text-white px-3 py-2 rounded shadow-lg text-sm">
        <p className="font-bold">{data.name}</p>
        <p>{`${data.value} repos`}</p>
      </div>
    );
  }
  return null;
};

const RepositoryAnalyticsDialog = ({ onClose, darkMode }) => {
  const [analyticsUsername, setAnalyticsUsername] = useState("");
  const [profile, setProfile] = useState(null);
  const [repos, setRepos] = useState([]);
  const [error, setError] = useState(null);

  const changeHandler = (e) => setAnalyticsUsername(e.target.value);

  const analyzeHandler = async () => {
    try {
      const { data: user } = await axios.get(
        `https://api.github.com/users/${analyticsUsername}`
      );
      const { data: repoArr } = await axios.get(
        `https://api.github.com/users/${analyticsUsername}/repos?per_page=100`
      );
      setProfile(user);
      setRepos(repoArr);
      setError(null);
    } catch {
      setError("No Profile with this username, Please check username");
      setProfile(null);
      setRepos([]);
    }
  };

  // Languages Data with total for percentage calculation
  const languageCounts = repos.reduce((acc, repo) => {
    if (repo.language) acc[repo.language] = (acc[repo.language] || 0) + 1;
    return acc;
  }, {});
  const totalRepos = repos.length;
  const languageData = Object.entries(languageCounts).map(([name, value]) => ({
    name,
    value,
    total: totalRepos,
  }));

  // Repository Size Data
  const sizeCounts = repos.reduce((acc, repo) => {
    const cat = getSizeCategory(repo.size * 1024);
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});
  const sizeData = Object.keys(sizeCounts).map((cat) => ({
    name: cat,
    value: sizeCounts[cat],
  }));

  // Stars Data - for horizontal bar chart
  const starData = repos
    .filter((repo) => repo.stargazers_count > 0)
    .sort((a, b) => b.stargazers_count - a.stargazers_count)
    .map((repo) => ({ name: repo.name, stars: repo.stargazers_count }))
    .slice(0, 10);

  // Most Active Repositories
  const mostActiveRepos = repos.sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
  );

  // Dynamically set modal height based on whether data is shown
  const isDataShown = profile && repos.length > 0;
  const modalHeight = isDataShown ? "h-[90vh]" : "h-auto";
  const maxWidth = isDataShown ? "max-w-6xl" : "max-w-4xl";

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center backdrop-blur bg-black bg-opacity-70">
      <div
        className={`rounded-2xl shadow-2xl ${
          darkMode ? "bg-[#202d36]" : "bg-white"
        } w-[98vw] ${maxWidth} ${modalHeight} flex flex-col relative overflow-hidden`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-6 right-6 p-2 text-2xl rounded-full z-10 transition duration-200 ${
            darkMode
              ? "text-white hover:bg-gray-700 hover:text-orange-400"
              : "text-gray-700 hover:bg-gray-200 hover:text-orange-500"
          }`}
          aria-label="Close dialog"
        >
          <IoMdClose className={darkMode ? "text-white" : "text-gray-700"} />
        </button>

        {/* Header and Search Container - All in one rounded box */}
        <div
          className={`flex-shrink-0 ${
            darkMode ? "bg-[#1a2f3f]" : "bg-gray-100"
          } ${!isDataShown ? "rounded-2xl" : "rounded-t-2xl"}`}
        >
          {/* Header */}
          <div className="flex items-center space-x-3 pt-6 pb-4 px-8">
            <CgIfDesign className="text-orange-400 text-3xl" />
            <span
              className={
                darkMode
                  ? "text-2xl text-white font-bold"
                  : "text-2xl text-gray-900 font-bold"
              }
            >
              Repository Analytics
            </span>
          </div>

          {/* Separator Line */}
          <div
            className={`${
              darkMode ? "border-[#2C3E50]" : "border-gray-300"
            } border-t mx-8`}
          ></div>

          {/* Search Bar */}
          <div className="flex items-center space-x-4 px-10 py-8">
            <input
              placeholder="Enter GitHub username..."
              value={analyticsUsername}
              onChange={changeHandler}
              className={
                darkMode
                  ? "flex-grow bg-[#374151] py-3 px-6 rounded-full text-white focus:outline-none text-base placeholder-gray-400 focus:ring-2 focus:ring-orange-400"
                  : "flex-grow bg-gray-200 py-3 px-6 rounded-full text-gray-700 focus:outline-none text-base placeholder-gray-500 focus:ring-2 focus:ring-orange-400"
              }
            />
            <button
              onClick={analyzeHandler}
              className="bg-gradient-to-r from-orange-400 to-pink-500 text-white rounded-full px-8 py-3 font-semibold text-lg shadow hover:scale-105 transition duration-200 flex-shrink-0"
            >
              Analyze
            </button>
          </div>
        </div>

        {error && (
          <div className="px-10 py-4">
            <ProfileError error={error} />
          </div>
        )}

        {/* User Info */}
        {profile && (
          <div
            className={`flex items-center space-x-5 px-10 py-6 ${
              darkMode ? "bg-[#202d36]" : "bg-white"
            }`}
          >
            <img
              src={profile.avatar_url}
              className={`w-20 h-20 rounded-full border-4 object-cover ${
                darkMode ? "border-[#2B3842]" : "border-gray-300"
              }`}
              alt={profile.name}
            />
            <div>
              <h2
                className={`text-3xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {profile.name}
              </h2>
              <div
                className={`font-semibold ${
                  darkMode ? "text-[#91a0ac]" : "text-gray-600"
                }`}
              >
                @{profile.login}
              </div>
              <div
                className={`text-base ${
                  darkMode ? "text-[#b3bdc7]" : "text-gray-700"
                }`}
              >
                {profile.public_repos} public repositories
              </div>
            </div>
          </div>
        )}
        {/* Analytics Grid with Scrollbar */}
        {profile && repos.length > 0 && (
          <div className="flex-1 overflow-y-auto px-10 pb-8">
            <div className="grid grid-cols-2 gap-8">
              {/* Donut Chart: Languages Distribution */}
              <div
                className={`p-8 rounded-xl shadow ${
                  darkMode ? "bg-[#23303b]" : "bg-blue-50"
                } flex flex-col`}
              >
                <div className="mb-6 text-xl font-bold text-orange-400">
                  Languages Distribution
                </div>
                <ResponsiveContainer width="100%" height={270}>
                  <PieChart>
                    <Pie
                      data={languageData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                      innerRadius={60}
                      label={false}
                    >
                      {languageData.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={PIE_COLORS[idx % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomLanguageTooltip />} />
                    <Legend
                      align="right"
                      verticalAlign="middle"
                      layout="vertical"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Pie Chart: Repository Size Distribution */}
              <div
                className={`p-8 rounded-xl shadow ${
                  darkMode ? "bg-[#23303b]" : "bg-blue-50"
                } flex flex-col`}
              >
                <div className="mb-6 text-xl font-bold text-orange-400">
                  Repository Size Distribution
                </div>
                <ResponsiveContainer width="100%" height={270}>
                  <PieChart>
                    <Pie
                      data={sizeData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                      label={false}
                    >
                      {sizeData.map((entry, idx) => (
                        <Cell
                          key={`size-cell-${idx}`}
                          fill={SIZE_COLORS[idx % SIZE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomSizeTooltip />} />
                    <Legend
                      align="right"
                      verticalAlign="middle"
                      layout="vertical"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Horizontal Bar Chart: Stars by Repository */}
              <div
                className={`col-span-1 p-8 rounded-xl shadow ${
                  darkMode ? "bg-[#23303b]" : "bg-blue-50"
                } flex flex-col`}
              >
                <div className="mb-6 text-xl font-bold text-orange-400">
                  Stars by Repository
                </div>
                <ResponsiveContainer width="100%" height={270}>
                  <BarChart
                    data={starData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 180, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#4a5568" />
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={2}
                      tick={{ fontSize: 11, fill: "#ffffff" }}
                    />
                    <Tooltip />
                    <Bar dataKey="stars" fill="#f6c342" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Most Active Repositories with Internal Scroll */}
              <div
                className={`col-span-1 p-8 rounded-xl shadow ${
                  darkMode ? "bg-[#23303b]" : "bg-blue-50"
                } flex flex-col`}
              >
                <div className="mb-6 text-xl font-bold text-orange-400">
                  Most Active Repositories
                </div>
                <div className="flex flex-col gap-5 overflow-y-auto max-h-[270px]">
                  {mostActiveRepos.map((repo) => (
                    <div
                      key={repo.id}
                      className={`rounded-lg p-4 ${
                        darkMode ? "bg-[#1a2730]" : "bg-gray-200"
                      } shadow flex flex-col`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-lg font-bold text-white">
                          {repo.name}
                        </span>
                        <span className="text-gray-400 text-sm">
                          {new Date(repo.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                      <span className="text-gray-400 text-sm mb-2">
                        {repo.description || "No description available"}
                      </span>
                      <div className="flex items-center gap-3 mt-1">
                        {repo.language && (
                          <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                            {repo.language}
                          </span>
                        )}
                        <span className="text-yellow-400 font-bold text-sm">
                          ⭐ {repo.stargazers_count}
                        </span>
                        <span className="text-green-400 font-bold text-sm">
                          🔀 {repo.forks_count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RepositoryAnalyticsDialog;
